import { designVariables } from "@studio3t/design-variables";
import { GenericAlert } from "@studio3t/generic-components";
import styled from "styled-components";

// Shared row chrome for the V2 stage-tab panels (Bridge_PipelineNavV2):
// the 50px section rows, their 40px List/item bodies, the indented
// schema rows and the pseudo-row typography. Extracted from
// StageNavPanel so the panel, the schema sub-section and the rename field
// can share one visual vocabulary.

const SEMANTIC = designVariables.ui.semantic;
const ITEM = designVariables.ui.sidebar.list.item;
// semantic/spacingxs (5px) isn't exported by design-variables yet.
const SPACING_XS = 5;
// spacings/xs (2px) — the schema body's left inset after the badge.
const SPACING_XXS = 2;

export { ITEM, SEMANTIC, SPACING_XS };

// SCHEMA sub-section wrapper: the schema rows of one stage block.
export const SubSection = styled.div``;
// 5px (semantic/spacingxs) padding around the unreferenced-schema alert.
export const AlertSlot = styled.div`
  padding: ${SPACING_XS}px;
  flex-shrink: 0;
`;
// The lib alert renders smaller than the Figma Alert component
// (14265-130655: Paragraph/Default/Regular 14px at 1.4, 10px padding) —
// sized up to match; the text styles inherit from the root. Shared by the
// panel alerts (unreferenced schema, edited input) and the save-warning
// modal messages — the state prop picks the variant.
export const StageAlert = styled(GenericAlert)`
  padding: ${SEMANTIC.spacings}px;
  gap: ${SEMANTIC.spacings}px;
  font-size: 14px;
  line-height: 1.4;
`;
// Bridge_PipelineNav/section/item — 50px row. $active carries the
// list-item active tint (selected row / the empty-state pseudo row);
// $zone hosts an inline drop zone with the 5px horizontal inset;
// $schema is the sub-level schema row (Figma 14327-153124 "Schema"
// variant): 10px inset on both sides and a 5px gap between the indent
// glyph, the badge and the body.
export const ItemRow = styled.div<{
  $active?: boolean;
  $zone?: boolean;
  $schema?: boolean;
  $interactive?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$schema ? SPACING_XS : SEMANTIC.spacings)}px;
  height: 50px;
  flex-shrink: 0;
  padding: ${(p) => rowPadding(p)};
  cursor: default;
  background: ${(p) =>
    p.$active ? p.theme.sidebarListItemActiveBackground : "transparent"};

  /* Hover carries the same list-item active tint as a selected row (Figma
     14442-316243) — only the 2px AccentBar stays selection-only. */
  ${(p) =>
    p.$interactive &&
    `&:hover {
      background: ${p.theme.sidebarListItemActiveBackground};
    }`}
`;
function rowPadding(p: { $zone?: boolean; $schema?: boolean }): string {
  if (p.$zone) return `0 ${SPACING_XS}px`;
  if (p.$schema) return `0 ${SEMANTIC.spacings}px`;
  return `0 ${SEMANTIC.spacings}px 0 0`;
}
// The 40px List/item body inside a row.
export const ItemBody = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: 40px;
  padding: 0 ${ITEM.paddingw}px;
`;
// 2px mainprimary accent spanning the full 50px row.
export const AccentBar = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  width: 2px;
  height: 100%;
  background: ${(p) => p.theme.semanticMainPrimary};
`;
// $fit stops the name from stretching so a trailing badge sits right after
// the text (Figma 14778-123822) — long names still ellipsize.
export const RowName = styled.span<{ $fit?: boolean }>`
  flex: ${(p) => (p.$fit ? "0 1 auto" : "1")};
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.semanticTextHeading};
`;
// Keeps a row's status badge at its natural size beside the name.
export const RowBadgeSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;
// The schema row's List/item body (Figma 15690-313156): sits after the
// "schema" badge with a 2px left inset, and its name reads in textbody
// rather than the stage rows' textheading.
export const SchemaItemBody = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: 40px;
  padding: 0 ${ITEM.paddingw}px 0 ${SPACING_XXS}px;

  ${RowName} {
    color: ${(p) => p.theme.semanticTextBody};
  }
`;
// The 18px ↳ glyph opening a schema row: list-item icon gray, textbody on
// the selected placeholder row (Figma 15683-307340).
export const SchemaIndent = styled.span<{ $emphasis?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: ${(p) =>
    p.$emphasis ? p.theme.semanticTextBody : p.theme.listItemIcon.default};
`;
// The "schema" badge between the indent glyph and the body.
export const SchemaBadgeSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;
// 20px icon wrap for the pseudo-row glyphs: dark on the emphasized
// empty-state row, disabled-gray on placeholders.
export const PseudoIconWrap = styled.span<{ $emphasis?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: ${(p) =>
    p.$emphasis ? p.theme.semanticTextHeading : p.theme.semanticTextDisabled};
`;
// Pseudo-row copy — 12px italic; textbody on the emphasized empty-state
// row, placeholder gray otherwise (14397-191575).
export const PseudoText = styled.span<{ $emphasis?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-style: italic;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) =>
    p.$emphasis ? p.theme.semanticTextBody : p.theme.semanticTextPlaceholder};
`;
